export interface ResolveWidgetHostingBaseUrlOptions {
  /** Explicit override (e.g. VITE_WIDGET_BASE_URL) */
  explicitUrl?: string | null;
  projectId?: string | null;
  isDev?: boolean;
  /** Browser page origin or request Origin / Referer origin */
  origin?: string | null;
}

const STAGING_WIDGET_HOST = '10seat-widget-staging.web.app';
const STAGING_WIDGET_PUBLIC_HOST = 'staging.reserve.10seat.com';
/** Custom domain first — Firebase Hosting (`reserve-10seat-com.web.app`) is the same app. */
const PRODUCTION_WIDGET_HOSTS = ['reserve.10seat.com', 'reserve-10seat-com.web.app'] as const;
const LOCAL_WIDGET_DEV_PORT = '5175';

function trimTrailingSlashes(value: string): string {
  return value.replace(/\/+$/, '');
}

function originFromKnownWidgetHost(origin: string): string | null {
  try {
    const url = new URL(origin.trim());
    const host = url.hostname.toLowerCase();

    if (host === STAGING_WIDGET_HOST || host === STAGING_WIDGET_PUBLIC_HOST) {
      return `${url.protocol}//${url.host}`;
    }

    if (PRODUCTION_WIDGET_HOSTS.includes(host as (typeof PRODUCTION_WIDGET_HOSTS)[number])) {
      return `${url.protocol}//${url.host}`;
    }

    if (host === 'localhost' || host === '127.0.0.1') {
      if (!url.port || url.port === LOCAL_WIDGET_DEV_PORT) {
        return `${url.protocol}//${url.host}`;
      }
    }
  } catch {
    return null;
  }

  return null;
}

/**
 * Resolve the 10Seat widget hosting base URL (no trailing slash).
 */
export function resolveWidgetHostingBaseUrl(
  options?: ResolveWidgetHostingBaseUrlOptions,
): string {
  const {
    explicitUrl,
    projectId = '',
    isDev = false,
    origin,
  } = options ?? {};

  const explicit = explicitUrl?.trim();
  if (explicit) {
    return trimTrailingSlashes(explicit);
  }

  const fromOrigin = origin ? originFromKnownWidgetHost(origin) : null;
  if (fromOrigin) {
    return fromOrigin;
  }

  if (isDev) {
    return `http://localhost:${LOCAL_WIDGET_DEV_PORT}`;
  }

  if (projectId === 'staging-10seat') {
    return `https://${STAGING_WIDGET_HOST}`;
  }

  return `https://${PRODUCTION_WIDGET_HOSTS[0]}`;
}

export function getWidgetHostingBaseUrl(projectId?: string | null): string {
  return resolveWidgetHostingBaseUrl({ projectId });
}

/**
 * Guest-facing widget host for emails and newsletters.
 * Never uses the backoffice origin or localhost — those links are clicked by guests.
 */
export function resolvePublicWidgetBookingBaseUrl(options?: {
  explicitUrl?: string | null;
  projectId?: string | null;
}): string {
  const explicit = options?.explicitUrl?.trim();
  if (explicit) {
    try {
      const host = new URL(explicit).hostname.toLowerCase();
      if (host !== 'localhost' && host !== '127.0.0.1') {
        return trimTrailingSlashes(explicit);
      }
    } catch {
      /* fall through to the public host */
    }
  }

  if (options?.projectId === 'staging-10seat') {
    return `https://${STAGING_WIDGET_PUBLIC_HOST}`;
  }

  return `https://${PRODUCTION_WIDGET_HOSTS[0]}`;
}

export function buildPublicBookingWidgetLink(params: {
  locationId: string;
  restaurantName?: string;
  projectId?: string | null;
  explicitUrl?: string | null;
}): string {
  const base = resolvePublicWidgetBookingBaseUrl({
    explicitUrl: params.explicitUrl,
    projectId: params.projectId,
  });
  const search = new URLSearchParams({
    location: params.locationId,
    theme: 'light',
    mode: 'embedded',
  });
  if (params.restaurantName) search.set('name', params.restaurantName);
  return `${base}/embedded.html?${search.toString()}`;
}

export function buildPublicExperienceWidgetLink(params: {
  locationId: string;
  marketingCode?: string;
  experienceId?: string;
  restaurantName?: string;
  /**
   * Pre-select the date (`YYYY-MM-DD`). Not needed when the experience runs on a single date — the
   * widget resolves that one itself.
   */
  date?: string;
  /**
   * Pre-select the party size. This is what lets a campaign link open an experience at a size the
   * venue's own widget floor would not offer: the experience's `min_party_size` lowers the floor for
   * that booking (see `shared/utils/experiencePartySizeFloor`), and this puts the guest straight on
   * it. Ignored unless it is a whole number in 1–100, matching what the widget accepts.
   */
  guests?: number;
  projectId?: string | null;
  explicitUrl?: string | null;
}): string {
  const base = resolvePublicWidgetBookingBaseUrl({
    explicitUrl: params.explicitUrl,
    projectId: params.projectId,
  });
  const search = new URLSearchParams({
    location: params.locationId,
    theme: 'light',
    mode: 'embedded',
  });
  if (params.restaurantName) search.set('name', params.restaurantName);
  if (params.marketingCode) search.set('10seat_code', params.marketingCode);
  else if (params.experienceId) search.set('10seat_exp', params.experienceId);
  if (params.date && /^\d{4}-\d{2}-\d{2}$/.test(params.date)) search.set('10seat_date', params.date);
  if (
    typeof params.guests === 'number' &&
    Number.isInteger(params.guests) &&
    params.guests > 0 &&
    params.guests <= 100
  ) {
    search.set('10seat_guests', String(params.guests));
  }
  return `${base}/embedded.html?${search.toString()}`;
}

export interface BuildWidgetTermsPageUrlOptions {
  lang?: string;
  projectId?: string | null;
  origin?: string | null;
  explicitBaseUrl?: string | null;
  isDev?: boolean;
}

export function buildWidgetTermsPageUrl(
  locationId: string,
  options?: BuildWidgetTermsPageUrlOptions,
): string {
  const base = resolveWidgetHostingBaseUrl({
    explicitUrl: options?.explicitBaseUrl,
    projectId: options?.projectId,
    isDev: options?.isDev,
    origin: options?.origin,
  });
  const params = new URLSearchParams({ location_id: locationId });
  if (options?.lang) {
    params.set('lang', options.lang);
  }
  return `${base}/terms.html?${params.toString()}`;
}

/**
 * Booking link for a gift voucher holder: the widget opens with "I have a gift voucher" ticked and
 * the code filled in, so the booking arrives at the venue already linked to the voucher.
 */
export function buildVoucherBookingWidgetLink(params: {
  locationId: string;
  voucherCode: string;
  restaurantName?: string;
  projectId?: string | null;
  explicitUrl?: string | null;
}): string {
  const url = new URL(buildPublicBookingWidgetLink(params));
  url.searchParams.set('10seat_voucher', params.voucherCode.trim().toUpperCase());
  return url.toString();
}
